/**
 * Sentry Event Sanitizer
 *
 * Strips common PII patterns (email addresses, phone numbers, JWT tokens) from
 * Sentry error event data before transmission. This function is designed to be
 * used as a `beforeSend` hook in Sentry SDK initialization.
 *
 * PII patterns cleaned:
 * - Email addresses (e.g., user@example.com)
 * - Phone numbers (e.g., +1 (555) 123-4567, +56912345678)
 * - JWT tokens (Bearer tokens, Auth tokens)
 */
/**
 * Regex pattern for matching email addresses.
 * Matches standard email format: local@domain.tld
 */
const emailPattern = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
/**
 * Regex pattern for matching phone numbers.
 * Matches both formatted numbers (with separators) and compact international formats.
 * To avoid false positives on hex strings, requires either separators between digits,
 * or an explicit country code prefix (+) for compact numbers.
 *
 * Formatted patterns:
 * - +1 (555) 123-4567 or +1 (555) 123-4567
 * - (555) 123-4567 or 555-123-4567
 * - +1-555-123-4567 or +1 555 123 4567
 * - +44 20 7946 0958
 *
 * Compact international patterns (with + prefix to avoid hex collision):
 * - +56912345678 (Chilean mobile)
 * - +441234567890 (UK)
 * - +33123456789 (France)
 *
 * Does NOT match:
 * - Pure digit runs without + prefix or separators (e.g., hex IDs like "1234567890abcdef")
 */
const phonePattern = /(\+\d{1,3}[-.\s])?\(?\d{3}\)?[-.\s]\d{3}[-.\s]\d{4,6}|(\+\d{1,3}[-.\s]\d{1,3}[-.\s]\d{3,4}[-.\s]\d{4})|\+\d{1,3}\d{8,14}/g;
/**
 * Regex pattern for matching JWT tokens and Bearer tokens.
 * Matches:
 * - Bearer eyJhbGc... (followed by >= 30 more chars to avoid false positives)
 * - eyJhbGc... (JWT base64-encoded object, minimum 36 chars: eyJ + 30+ payload chars)
 *
 * The first token (eyJ) is always present in real JWTs because it encodes '{"'.
 * The minimum length (30+ characters after eyJ) prevents matching short words like
 * "eyes", "eyebrows", etc., that coincidentally start with "ey".
 */
const jwtPattern = /(Bearer\s+[A-Za-z0-9\-_.]+|eyJ[A-Za-z0-9\-_.]{30,})/g;
/**
 * Sanitizes a string by redacting common PII patterns.
 *
 * @param str - The string to sanitize
 * @returns The sanitized string with PII replaced by placeholders
 */
function sanitizeString(str) {
    if (typeof str !== 'string') {
        return str;
    }
    let sanitized = str;
    // Replace email addresses
    sanitized = sanitized.replace(emailPattern, '[EMAIL]');
    // Replace phone numbers
    sanitized = sanitized.replace(phonePattern, '[PHONE]');
    // Replace JWT/Bearer tokens
    sanitized = sanitized.replace(jwtPattern, '[TOKEN]');
    return sanitized;
}
/**
 * Recursively sanitizes an object's string values by redacting PII.
 * Tracks visited objects to prevent infinite recursion on circular references.
 *
 * @param obj - The object to sanitize
 * @param visited - WeakSet of already-visited objects (internal use)
 * @returns A new object with sanitized string values
 */
function sanitizeObject(obj, visited) {
    if (obj === null || obj === undefined) {
        return obj;
    }
    if (typeof obj === 'string') {
        return sanitizeString(obj);
    }
    if (typeof obj !== 'object') {
        return obj;
    }
    // Initialize visited set on first call
    if (!visited) {
        visited = new WeakSet();
    }
    // Detect and prevent circular references
    if (visited.has(obj)) {
        return '[Circular]';
    }
    // Mark this object as visited
    visited.add(obj);
    if (Array.isArray(obj)) {
        return obj.map((item) => sanitizeObject(item, visited));
    }
    const sanitized = {};
    for (const [key, value] of Object.entries(obj)) {
        sanitized[key] = sanitizeObject(value, visited);
    }
    return sanitized;
}
/**
 * Sentry beforeSend callback that sanitizes error events by removing PII.
 *
 * This function is designed to be used as the `beforeSend` hook in Sentry SDK
 * initialization to ensure that email addresses, phone numbers, and JWT tokens
 * are redacted from error events before they are transmitted to Sentry.
 *
 * @param event - The Sentry event to sanitize
 * @returns The sanitized event, or the original event if no sensitive data was found
 *
 * @example
 * ```typescript
 * import { sanitizeErrorEvent } from '@packages/shared-utils';
 * import * as Sentry from '@sentry/angular';
 *
 * Sentry.init({
 *   dsn: '...',
 *   beforeSend: sanitizeErrorEvent,
 * });
 * ```
 */
export function sanitizeErrorEvent(event) {
    if (!event) {
        return event;
    }
    const sanitized = Object.assign({}, event);
    // Sanitize message
    if (sanitized.message) {
        sanitized.message = sanitizeString(sanitized.message);
    }
    // Sanitize entire exception subtree recursively (covers value, stacktrace frames,
    // filename, pre_context, post_context, vars, mechanism.data, and any custom fields)
    if (sanitized.exception) {
        sanitized.exception = sanitizeObject(sanitized.exception);
    }
    // Sanitize breadcrumbs
    if (sanitized.breadcrumbs) {
        sanitized.breadcrumbs = sanitized.breadcrumbs.map((breadcrumb) => {
            const sanitizedBreadcrumb = Object.assign({}, breadcrumb);
            if (sanitizedBreadcrumb.message) {
                sanitizedBreadcrumb.message = sanitizeString(sanitizedBreadcrumb.message);
            }
            if (sanitizedBreadcrumb.data) {
                sanitizedBreadcrumb.data = sanitizeObject(sanitizedBreadcrumb.data);
            }
            return sanitizedBreadcrumb;
        });
    }
    // Sanitize request (all fields including headers, url, query_string, etc.)
    if (sanitized.request) {
        sanitized.request = sanitizeObject(sanitized.request);
    }
    // Sanitize all other fields recursively (including 'extra' and any arbitrary nested objects)
    // Exclude Sentry internal metadata fields that should never contain PII and must not be corrupted
    const sentryMetadataKeys = [
        'event_id',
        'timestamp',
        'level',
        'platform',
        'sdk',
        'logger',
        'server_name',
        'release',
        'dist',
        'environment',
        'fingerprint',
    ];
    for (const key of Object.keys(sanitized)) {
        if (key !== 'message' &&
            key !== 'exception' &&
            key !== 'breadcrumbs' &&
            key !== 'request' &&
            !sentryMetadataKeys.includes(key)) {
            sanitized[key] = sanitizeObject(sanitized[key]);
        }
    }
    return sanitized;
}
